import { useState } from "react";
import { useHistory } from "react-router-dom";
import { BlueprintOperationType } from "../../../models/Blueprints";
import DropdownFormField from "../../blueprint-editor/right-panel/DropdownFormField";
import { ScraperDataType } from "../../scraper/types";
import { createScraper } from "../../scraper/utils/ScraperAPIClient";
import { Button, ButtonVariant, Dialog, Text } from "@merge-api/merge-javascript-shared";
import { Plus } from "lucide-react";

type Props = {
  integrationID: string;
};

const getNameForScraperDataType = (dataType: ScraperDataType) => {
  switch (dataType) {
    case ScraperDataType.AUTH_DATA:
      return "Linking Flow Authentication";
    case ScraperDataType.COMMON_MODEL_DATA:
      return "Common Model Creation/Modification/Deletion";
  }
};

const ScrapersTableCreateButton = ({ integrationID }: Props) => {
  const history = useHistory();
  const [isShowingConfirmCreateScraperModal, setIsShowingConfirmCreateScraperModal] = useState<
    boolean
  >(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scraperDataType, setScraperDataType] = useState(ScraperDataType.COMMON_MODEL_DATA);

  const [operationType, setOperationType] = useState(BlueprintOperationType.FETCH);

  return (
    <>
      <Dialog
        title="Confirm scraper creation"
        open={isShowingConfirmCreateScraperModal ?? false}
        onClose={() => setIsShowingConfirmCreateScraperModal(false)}
        primaryButtonText="Create scraper and blueprint"
        primaryButtonLoading={isLoading}
        primaryButtonVariant={ButtonVariant.PrimaryGray}
        onPrimaryButtonClick={() => {
          setIsLoading(true);
          createScraper({
            name: "Default Name",
            scraperDataType,
            operationType,
            integrationID,
            onSuccess: (data) => {
              history.push(
                `/integrations/${integrationID}/scrapers/${data.id}/versions/${data.version_id}/editor`
              );
            },
          });
        }}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsShowingConfirmCreateScraperModal(false)}
        variant="md"
      >
        <Text as="p">Creating a scraper will also create an associated scraping blueprint.</Text>
        <Text as="p">
          If you mark this scraper for authentication, it will be called after the end user inputs
          login credentials in the linking flow to pull authentication credentials.
        </Text>
        <DropdownFormField
          choices={Object.keys(BlueprintOperationType).map((operationType) => ({
            name: operationType,
            id: operationType,
          }))}
          currentValue={operationType}
          placeholder={"Set operation type"}
          title="Operation type"
          subtitle="Operation type of the associated blueprint."
          onChange={(e) => setOperationType(e.target.value)}
        />
        <DropdownFormField
          choices={(Object.keys(ScraperDataType) as Array<ScraperDataType>).map((dataType) => ({
            name: getNameForScraperDataType(dataType),
            id: dataType,
          }))}
          currentValue={scraperDataType}
          placeholder={"Set scraper function"}
          title="Scraper data type"
          subtitle="Mark whether this scraper is used for creating/modifying/deleting common models or for pulling authentication credentials in the linking flow."
          onChange={(e) => setScraperDataType(e.target.value)}
        />
      </Dialog>
      <Button
        size="sm"
        variant={ButtonVariant.TertiaryWhite}
        onClick={() => setIsShowingConfirmCreateScraperModal(true)}
        leftIcon={<Plus size={12} />}
      >
        Create scraper
      </Button>
    </>
  );
};

export default ScrapersTableCreateButton;
